import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Home.css';
import {
  FaRocket, FaStar, FaCamera, FaEnvelope, FaMapMarkerAlt, FaInstagram, FaYoutube, FaPhone, FaWhatsapp, FaUsers, FaFilm, FaSprayCan, FaUserTie, FaPaintBrush, FaGift
} from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import { eventPackagesData } from '../utils/eventPackagesData';

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleSections] = useState(() => new Set(['packages-section', 'services-section', 'tools-section', 'why-choose-section', 'cta-section', 'contact-section']));
  const sectionRefs = useRef({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { addToCart } = useCart();
  const [stuckFlipped, setStuckFlipped] = useState(() => new Set());

  const handleServiceNav = (path, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(path);
  };

  const toggleCardFlipped = (index) => {
    setStuckFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleCardClick = (e) => {
    if (e.target.closest('a')) return;
    e.preventDefault();
    e.stopPropagation();
    const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);
    if (!Number.isNaN(index)) toggleCardFlipped(index);
  };

  const handleCardKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);
    if (!Number.isNaN(index)) toggleCardFlipped(index);
  };

  // Hero background image
  const heroImages = useMemo(() => [
    `${process.env.PUBLIC_URL || ''}/assets/background/background2.jpeg`
  ], []);

  // Preload hero images for faster loading
  useEffect(() => {
    heroImages.forEach((imageUrl, index) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      // High priority for first image (above the fold)
      if (index === 0) {
        link.setAttribute('fetchpriority', 'high');
      }
      document.head.appendChild(link);
    });

    return () => {
      // Cleanup preload links on unmount
      document.querySelectorAll('link[rel="preload"][as="image"]').forEach(link => {
        if (heroImages.includes(link.href)) {
          link.remove();
        }
      });
    };
  }, [heroImages]);


  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedProduct(null), 300);
  };

  const handleAddToCart = (product, event) => {
    if (event) event.stopPropagation();
    addToCart(product);
  };

  // Form handling
  const handleFormChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // eslint-disable-next-line no-unused-vars
      await fetch("https://script.google.com/macros/s/AKfycbw5AMZlHOjaa_GfCm1m7MnJ66bH0uY_r5GDCbE6iPgrOhk-3T-hEW8J4v0S_w_U4CY/exec", {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      // Message sent successfully
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      // Error sending message - user will be notified through UI
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Camera Rentals Bangalore | BookMyCamera – DSLR, Cinema Camera & Gear Rental"
        description="Camera rentals Bangalore – Rent DSLRs, cinema cameras, lenses, lights & gear. Professional photography & videography, wedding shoots, and same-day camera hire in Bangalore."
        keywords="camera rentals bangalore, camera rental bangalore, bangalore camera rental, dslr rental bangalore, camera hire bangalore, cinema camera rental bangalore, lens rental bangalore, photography equipment rental bangalore, bookmycamera, wedding photography bangalore, camera gear rental bangalore"
      />
      <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url('${image}')` }}
            ></div>
          ))}
        </div>

        <div className="hero-overlay"></div>

        <div className="hero-content">
          <div className="hero-left-content">
            <div className="hero-text-container">
              <h1 className="hero-title">“From Casting to Camera — All in One Place.” </h1>
              <p className="hero-subtitle">
              Your complete filmmaking ecosystem — casting, crew, creators, and camera bookings made simple.
              </p>
            </div>

            <div className="hero-features">
              <div className="feature-box">
                <div className="feature-number">50+</div>
                <div className="feature-label">Cameras</div>
              </div>
              <div className="feature-box">
                <div className="feature-number">24/7</div>
                <div className="feature-label">Support</div>
              </div>

            </div>
          </div>

          <div className="hero-right-content">
            <div className="camera-product">
            </div>
          </div>
        </div>
      </section>

      {/* Event packages preview */}
      <section
        id="packages-section"
        ref={(el) => { sectionRefs.current['packages-section'] = el; }}
        className={`packages-preview-section ${visibleSections.has('packages-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <h2 className="gradient-text-primary">Event Packages</h2>
          <p className="subtitle packages-preview-subtitle">
            Ready-made bundles for weddings, celebrations, and corporate events
          </p>
          <div className="home-packages-grid">
            {eventPackagesData.slice(0, 3).map((pkg) => {
              const base = process.env.PUBLIC_URL || '';
              const imgPath = pkg.image.startsWith('/') ? pkg.image : `/${pkg.image}`;
              return (
                <article key={pkg.id} className="home-package-card">
                  <div
                    className="home-package-card-image"
                    style={{ backgroundImage: `url('${base}${imgPath}')` }}
                    role="img"
                    aria-label={pkg.name}
                  />
                  <div className="home-package-card-body">
                    <h3>{pkg.name}</h3>
                    <p className="home-package-duration">{pkg.duration}</p>
                    <p className="home-package-blurb">{pkg.features.slice(0, 2).join(' · ')}</p>
                    <p className="home-package-price">
                      ₹{pkg.price.toLocaleString('en-IN')}
                      {pkg.originalPrice ? (
                        <span className="home-package-price-was">₹{pkg.originalPrice.toLocaleString('en-IN')}</span>
                      ) : null}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="home-packages-cta">
            <button
              type="button"
              className="btn btn-primary home-packages-btn"
              onClick={() => navigate('/event-packages')}
            >
              <FaGift aria-hidden />
              View all packages
            </button>
          </div>
        </div>
      </section>

      {/* Tools Section - Pro Gear & Shoot Kits */}
      <section
        id="tools-section"
        ref={(el) => sectionRefs.current['tools-section'] = el}
        className={`tools-section ${visibleSections.has('tools-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <h2>Pro Gear & Shoot Kits</h2>
          <div className="tools-grid">
            <div className="tool-card" onClick={() => navigate('/products?search=camera')} aria-label="Camera" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/cemare_.png`} 
                  alt="Camera" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Camera</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=lens')} aria-label="Lens" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/lens.png`} 
                  alt="Lens" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Lens</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products')} aria-label="Special Gears" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/other_equipments.png`} 
                  alt="Special Gears" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Special Gears</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=lights')} aria-label="Lights" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/lights.png`} 
                  alt="Lights" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Lights</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=audio')} aria-label="Audio" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/audio_.png`} 
                  alt="Audio" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Audio</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=studio')} aria-label="Studio" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/Studio.JPG`} 
                  alt="Studio" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Studio</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=gimbal')} aria-label="Gimbals & Rigs" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/gimbal_and_rig.png`} 
                  alt="Gimbals & Rigs" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Gimbals & Rigs</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=tripod')} aria-label="Tripods & Sliders" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/shoot kit/tripod_and_sliders.png`} 
                  alt="Tripods & Sliders" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Tripods & Sliders</h3>
              </div>
            </div>

            <div className="tool-card" onClick={() => navigate('/products?search=monitor')} aria-label="Monitors" style={{cursor: 'pointer'}}>
              <div className="tool-card-image">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/Monitor/lilliput-a7s-7-field-monitor-removebg-preview.png`} 
                  alt="Monitors" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="tool-card-content">
                <h3>Monitors</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section
        id="services-section"
        ref={(el) => sectionRefs.current['services-section'] = el}
        className={`services-section ${visibleSections.has('services-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <h2 className="gradient-text-primary">Services We Offer</h2>
          <p className="subtitle">End-to-end solutions for shoots and rentals</p>
          <div className="services-grid">
            {/* Casting Crew */}
            <div
              className={`service-flip-card ${stuckFlipped.has(0) ? 'flipped' : ''}`}
              data-index={0}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(0)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Casting Crew</h3>
                    <p>Professional casting services with experienced crew members.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/services" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/services', e)}>
                        <FaUsers />View Services
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Casting Crew</h3>
                    <p>Hire experienced professionals for your production team.</p>
                    <a href="/services" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/services', e)}>
                      <FaUsers />View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Cinema Autography */}
            <div
              className={`service-flip-card ${stuckFlipped.has(1) ? 'flipped' : ''}`}
              data-index={1}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(1)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Cinema Autography</h3>
                    <p>Professional cinematography services with cutting-edge equipment.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/services" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/services', e)}>
                        <FaFilm />View Services
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Cinema Autography</h3>
                    <p>Get cinematic quality with expert cinematography services.</p>
                    <a href="/services" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/services', e)}>
                      <FaFilm />View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Makeup Artists */}
            <div
              className={`service-flip-card ${stuckFlipped.has(2) ? 'flipped' : ''}`}
              data-index={2}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(2)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Makeup Artists</h3>
                    <p>Professional makeup artists for film, fashion, and special effects.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/services" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/services', e)}>
                        <FaSprayCan />View Services
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Makeup Artists</h3>
                    <p>Book professional makeup artists for your production.</p>
                    <a href="/services" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/services', e)}>
                      <FaSprayCan />View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Graphic Designers */}
            <div
              className={`service-flip-card ${stuckFlipped.has(3) ? 'flipped' : ''}`}
              data-index={3}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(3)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Graphic Designers</h3>
                    <p>Creative graphic designers for posters, titles, and visual branding.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/services" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/services', e)}>
                        <FaPaintBrush />View Services
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Graphic Designers</h3>
                    <p>Hire talented graphic designers for your creative projects.</p>
                    <a href="/services" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/services', e)}>
                      <FaPaintBrush />View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Directors */}
            <div
              className={`service-flip-card ${stuckFlipped.has(4) ? 'flipped' : ''}`}
              data-index={4}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(4)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Directors</h3>
                    <p>Experienced film and commercial directors for your projects.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/services" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/services', e)}>
                        <FaUserTie />View Services
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Directors</h3>
                    <p>Bring your vision to life with experienced directors.</p>
                    <a href="/services" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/services', e)}>
                      <FaUserTie />View Services
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Film Gear Rental */}
            <div
              className={`service-flip-card ${stuckFlipped.has(5) ? 'flipped' : ''}`}
              data-index={5}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
              aria-pressed={stuckFlipped.has(5)}
            >
              <div className="service-flip-card-inner">
                <div className="service-flip-card-front">
                  <div className="service-image" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800&h=450&fit=crop&auto=format&q=80')" }}></div>
                  <div className="service-front-content">
                    <h3>Film Gear Rental</h3>
                    <p>Professional camera equipment and film gear rentals for your productions.</p>
                    <span className="service-front-cta-mobile">
                      <a href="/products" className="btn btn-primary btn-flip btn-flip-mobile" onClick={(e) => handleServiceNav('/products', e)}>
                        <FaCamera />View Products
                      </a>
                    </span>
                  </div>
                </div>
                <div className="service-flip-card-back">
                  <div className="service-back-content">
                    <h3>Film Gear Rental</h3>
                    <p>Rent professional cameras, lenses, lighting, and all film production equipment.</p>
                    <a href="/products" className="btn btn-primary btn-flip" onClick={(e) => handleServiceNav('/products', e)}>
                      <FaCamera />View Products
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Book My Camera */}
      <section
        id="why-choose-section"
        ref={(el) => sectionRefs.current['why-choose-section'] = el}
        className={`why-choose-section ${visibleSections.has('why-choose-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <h2 className="gradient-text-primary">Why Choose Book My Camera?</h2>
          <p className="subtitle">We make professional photography accessible to everyone</p>
          <div className="why-choose-grid">
            <div className="why-choose-card">
              <div className="why-choose-icon blue">
                <FaStar />
              </div>
              <h3>Quality Guaranteed</h3>
              <p> We ensure top quality by maintaining all our equipment professionally and keeping it in excellent condition.</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon green">
                <FaStar />
              </div>
              <h3>Flexible Rental</h3>
              <p> Choose from daily, weekly, or monthly rentals — enjoy lower rates the longer you rent.</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon orange">
                <FaRocket />
              </div>
              <h3>Convenient Delivery</h3>
              <p> Enjoy free home delivery across Bangalore — no store visit needed!
              Prefer picking it up yourself? You're always welcome to visit our store and take it for rent.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA: Custom Quote */}
      <section
        id="cta-section"
        ref={(el) => sectionRefs.current['cta-section'] = el}
        className={`cta-section ${visibleSections.has('cta-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2 className="gradient-text-primary">Need a Custom Shoot Kit?</h2>
              <p>Tell us your brief and budget — we'll assemble the perfect package.</p>
            </div>
            <div className="cta-button">
              <button onClick={() => navigate('/contact')} className="cta-btn">
                <span>Get a Quote</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact-section"
        ref={(el) => sectionRefs.current['contact-section'] = el}
        className={`contact-section ${visibleSections.has('contact-section') ? 'animate-in' : 'animate-out'}`}
      >
        <div className="container">
          <div className="contact-header">
            <h2 className="gradient-text-primary">Get In Touch</h2>
            <p className="subtitle">Ready to bring your vision to life? We're here to help you choose the perfect gear.</p>
          </div>

          <div className="contact-content">
            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-header">
                <div className="form-icon">
                  <FaEnvelope />
                </div>
                <div className="form-title">
                  <h3>Send us a message</h3>
                  <p>We'll get back to you within 24 hours</p>
                </div>
              </div>

              <form onSubmit={handleFormSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Your Message"
                    rows="4"
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  <FaEnvelope />
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-section">
              <div className="contact-info-item">
                <div className="info-icon visit">
                  <FaMapMarkerAlt />
                </div>
                <div className="info-content">
                  <h4>Visit Us</h4>
                  <p className="address-text">
                    {process.env.REACT_APP_ADDRESS ? (
                      (() => {
                        const parts = process.env.REACT_APP_ADDRESS.split(',').map(p => p.trim());
                        // Format: Group parts logically
                        // First 3 parts = street address, next 2 = area, last 2 = city/state
                        const street = parts.slice(0, 3).join(', ');
                        const area = parts.slice(3, 5).join(', ');
                        const city = parts.slice(5).join(', ');
                        return (
                          <>
                            {street}<br />
                            {area}<br />
                            {city}
                          </>
                        );
                      })()
                    ) : (
                      <>
                        23, 12th Main Rd, 4th Block East,<br />
                        KV Layout, Jayanagar,<br />
                        Bengaluru, Karnataka 560011
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="info-icon call">
                  <FaPhone />
                </div>
                <div className="info-content">
                  <h4>Call Us</h4>
                  <p className="contact-phone-list">
                    {process.env.REACT_APP_PHONE_NUMBER ? (
                      <a
                        href={`tel:+91${String(process.env.REACT_APP_PHONE_NUMBER).replace(/\D/g, '')}`}
                      >
                        +91 {process.env.REACT_APP_PHONE_NUMBER}
                      </a>
                    ) : null}
                    {process.env.REACT_APP_SECONDARY_NUMBER ? (
                      <>
                        {process.env.REACT_APP_PHONE_NUMBER ? <br /> : null}
                        <a
                          href={`tel:+91${String(process.env.REACT_APP_SECONDARY_NUMBER).replace(/\D/g, '')}`}
                        >
                          +91 {process.env.REACT_APP_SECONDARY_NUMBER}
                        </a>
                      </>
                    ) : null}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="info-icon email">
                  <FaEnvelope />
                </div>
                <div className="info-content">
                  <h4>Email Us</h4>
                  <p>{process.env.REACT_APP_EMAIL || 'bookmycamera@gmail.com'}</p>
                </div>
              </div>

              <div className="social-section">
                <h4>Follow Us</h4>
                <div className="social-links">
                  <a href={process.env.REACT_APP_INSTAGRAM || "https://www.instagram.com/bmc__events?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="}
                    className="social-link instagram"
                    target="_blank" rel="noopener noreferrer">
                    <FaInstagram />
                  </a>
                  <a href={process.env.REACT_APP_YOUTUBE?.trim() || "#"}
                    className="social-link youtube"
                    target="_blank" rel="noopener noreferrer">
                    <FaYoutube />
                  </a>
                  <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP || process.env.REACT_APP_PHONE_NUMBER}?text=Hello%20I%20would%20like%20to%20know%20about%20the%20pricing.`}
                    className="social-link whatsapp">
                    <FaWhatsapp />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for Product Details */}
      {showModal && selectedProduct && (
        <div className={`modal-overlay ${showModal ? 'active' : ''}`} onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="modal-header">
              <div className="modal-icon">
                <img 
                  src={selectedProduct.image} 
                  alt={selectedProduct.name} 
                  className="modal-product-img"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="modal-title-section">
                <span className="modal-category">{selectedProduct.category}</span>
                <h2>{selectedProduct.name}</h2>
                <div className="modal-price-section">
                  {selectedProduct.discount > 0 && (
                    <span className="modal-original-price">{selectedProduct.category !== 'Studio' ? '₹' : ''}{selectedProduct.originalPrice}</span>
                  )}
                  <span className="modal-price">{selectedProduct.category !== 'Studio' ? '₹' : ''}{selectedProduct.price}<span className="price-period">/day</span></span>
                  {selectedProduct.discount > 0 && (
                    <span className="modal-discount-badge">Save {selectedProduct.discount}%</span>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>Description</h3>
                <p>{selectedProduct.fullDescription}</p>
              </div>

              <div className="modal-section">
                <h3>Key Features</h3>
                <ul className="features-list">
                  {selectedProduct.features.map((feature, index) => (
                    <li key={index}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="modal-section">
                <h3>Specifications</h3>
                <div className="specifications-grid">
                  {Object.entries(selectedProduct.specifications).map(([key, value]) => (
                    <div key={key} className="spec-item">
                      <span className="spec-label">{key}:</span>
                      <span className="spec-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="modal-cart-btn"
                  onClick={() => handleAddToCart(selectedProduct)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default Home;
